import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store/Store';
import { Quiz } from '../models/Quiz';
import { Link, useSearchParams } from 'react-router-dom';
import { useLocale } from '../context/LocaleContext';
import { usePageMeta } from '../hooks/usePageMeta';
import './QuizSelectionPage.css';

type TabType = 'medborgerskab' | 'indfødsret';

const QuizSelectionPage: React.FC = () => {
    const { pathFor } = useLocale();
    usePageMeta('Vejen til dansk statsborgerskab', 'Practice tests for the Danish citizenship exam (Medborgerskabsprøven) and indfødsretsprøven. Past exam questions to prepare for Danish naturalization.');
    const quizzesData = useSelector((state: RootState) => state.quizzes.data);
    const [searchParams, setSearchParams] = useSearchParams();
    const tabParam = searchParams.get('tab') as TabType | null;
    const [activeTab, setActiveTab] = useState<TabType>(tabParam || 'medborgerskab');

    useEffect(() => {
        if (tabParam && (tabParam === 'medborgerskab' || tabParam === 'indfødsret')) {
            setActiveTab(tabParam);
        } else if (!tabParam) {
            setSearchParams({ tab: 'medborgerskab' }, { replace: true });
        }
    }, [tabParam, setSearchParams]);

    const extractYear = (title: string): string => {
        const match = title.match(/\d{4}/);
        return match ? match[0] : '';
    };

    const filteredQuizzes = (quizzesData || []).filter((quiz: Quiz) => {
        if (activeTab === 'medborgerskab') {
            return quiz.title.includes('Medborgerskabsprøven');
        } else {
            return quiz.title.includes('Indfødsretsprøven');
        }
    });

    const sortedQuizzes = [...filteredQuizzes].sort((a, b) => {
        const yearA = parseInt(extractYear(a.title));
        const yearB = parseInt(extractYear(b.title));
        if (yearA !== yearB) return yearB - yearA;
        // If same year, sort by season (Vinter before Sommer)
        if (a.title.includes('Vinter') && b.title.includes('Sommer')) return -1;
        if (a.title.includes('Sommer') && b.title.includes('Vinter')) return 1;
        return 0;
    });

    return (
        <div className='quiz-selection-container'>
            <Link to={pathFor('')} className="quiz-back-link">
                <span className="quiz-back-arrow">←</span>
                <span>Back to home</span>
            </Link>
            <div className="quiz-selection-header">
                <div className="quiz-selection-title-wrapper">
                    <span className="quiz-selection-icon">🇩🇰</span>
                    <h1 className="quiz-selection-title">Vejen til Dansk Statsborgerskab</h1>
                </div>
                <p className="quiz-selection-disclaimer">
                    Hvis du bemærker problemer med prøverne, så send mig en email på <a href="mailto:b@brianna.dk">b@brianna.dk</a>.
                </p>
            </div>
            <div className="quiz-layout">
                <div className="quiz-sidebar">
                    <Link
                        to="/projects/vejen-til-dansk-statsborgerskab?tab=medborgerskab"
                        className={`quiz-tab ${activeTab === 'medborgerskab' ? 'active' : ''}`}
                        onClick={(e) => {
                            e.preventDefault();
                            setSearchParams({ tab: 'medborgerskab' });
                            setActiveTab('medborgerskab');
                        }}
                    >
                        Medborgerskabsprøver
                    </Link>
                    <Link
                        to="/projects/vejen-til-dansk-statsborgerskab?tab=indfødsret"
                        className={`quiz-tab ${activeTab === 'indfødsret' ? 'active' : ''}`}
                        onClick={(e) => {
                            e.preventDefault();
                            setSearchParams({ tab: 'indfødsret' });
                            setActiveTab('indfødsret');
                        }}
                    >
                        Indfødsretsprøver
                    </Link>
                </div>
                <div className="quiz-content">
                    <h2 className="quiz-content-title">
                        {activeTab === 'medborgerskab' ? 'Medborgerskabsprøver' : 'Indfødsretsprøver'}
                    </h2>
                    <p className="quiz-content-attribution">
                        {activeTab === 'indfødsret' ? (
                            <>
                                Hentet fra prøve-PDF'er og retteark fra{' '}
                                <a href="https://danskogproever.dk/borger/indfoedsretsproeve-statsborgerskab/forberedelse-til-indfoedsretsproeven/#accordion-tidligere-indfodsretsprover" target="_blank" rel="noopener noreferrer">
                                    danskogproever.dk
                                </a>
                            </>
                        ) : (
                            <>
                                Hentet fra prøve-PDF'er og retteark fra{' '}
                                <a href="https://danskogproever.dk/borger/medborgerskabsproeve-permanent-ophold/forberedelse-til-medborgerskabsproeven/#accordion-tidligere-medborgerskabsprover" target="_blank" rel="noopener noreferrer">
                                    danskogproever.dk
                                </a>
                            </>
                        )}
                    </p>
                    {activeTab === 'indfødsret' && sortedQuizzes.length === 0 ? (
                        <div className="quiz-placeholder">
                            <p>Indfødsretsprøver kommer snart...</p>
                        </div>
                    ) : (
                        <div className="quiz-table-container">
                            <table className="quiz-table">
                                <thead>
                                    <tr>
                                        <th>År</th>
                                        <th>Prøve</th>
                                        <th>Spørgsmål</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedQuizzes.map((quiz: Quiz, index: number) => (
                                        <tr key={index}>
                                            <td className="quiz-year">{extractYear(quiz.title)}</td>
                                            <td className="quiz-name">
                                                <Link to={`/projects/vejen-til-dansk-statsborgerskab/start?id=${quiz.id}`} className="quiz-link">
                                                    {quiz.title}
                                                </Link>
                                            </td>
                                            <td className="quiz-questions">{quiz.questions?.length || 0}</td>
                                            <td className="quiz-link-cell">
                                                <Link to={`/projects/vejen-til-dansk-statsborgerskab/start?id=${quiz.id}`} className="quiz-external-link">
                                                    Start →
                                                </Link>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default QuizSelectionPage;
